import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  DefaultButton,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  DatePicker,
  Dropdown,
  IDropdownOption,
  Panel,
  PanelType,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Icon,
} from '@fluentui/react';
import {
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import { costService } from '../../services/costService';
import { CostSummary, CostTrend, ModelUsage, UserUsage } from '../../types/cost';
import { OrganizationUsageProgress } from './OrganizationUsageProgress';
import { buildApiUrl } from '../../config/apiConfig';

interface CostDashboardProps {
  className?: string;
}

interface DateRange {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

interface Filters {
  dateRange: DateRange;
  selectedUser: string | null;
  selectedModel: string | null;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: string;
  trend?: number;
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  trend,
  color = '#667eea'
}) => (
  <div style={{ 
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    padding: '24px',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    minWidth: '280px',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '140px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  }}>
    {/* Background decoration */}
    <div style={{
      position: 'absolute',
      top: '-20px',
      right: '-20px',
      width: '80px',
      height: '80px',
      background: `linear-gradient(135deg, ${color}20, ${color}10)`,
      borderRadius: '50%',
    }} />
    
    <div style={{ position: 'relative', zIndex: 1 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
        <Stack>
          <Text variant="medium" style={{ 
            color: '#4a5568', 
            fontWeight: '500',
            marginBottom: '8px'
          }}>
            {title}
          </Text>
          {icon && (
            <div style={{
              background: `linear-gradient(135deg, ${color}, ${color}dd)`,
              borderRadius: '12px',
              padding: '8px',
              display: 'inline-block',
              marginBottom: '12px'
            }}>
              <Icon 
                iconName={icon} 
                style={{ 
                  fontSize: '20px', 
                  color: '#ffffff'
                }} 
              />
            </div>
          )}
        </Stack>
        {trend !== undefined && (
          <div style={{
            background: trend >= 0 ? 'rgba(46, 204, 113, 0.2)' : 'rgba(231, 76, 60, 0.2)',
            padding: '4px 8px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Icon 
              iconName={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} 
              style={{ fontSize: '12px', color: trend >= 0 ? '#2ecc71' : '#e74c3c' }} 
            />
            <Text variant="small" style={{ color: trend >= 0 ? '#2ecc71' : '#e74c3c', fontWeight: '600' }}>
              {Math.abs(trend)}%
            </Text>
          </div>
        )}
      </Stack>
      
      <Text style={{ 
        fontWeight: '700', 
        margin: '8px 0',
        color: '#2d3748',
        fontSize: '28px',
        lineHeight: '1.2'
      }}>
        {value}
      </Text>
      
      {subtitle && (
        <Text variant="small" style={{ 
          color: '#718096',
          fontSize: '13px'
        }}>
          {subtitle}
        </Text>
      )}
    </div>
  </div>
);

const ChartWidget: React.FC<{
  title: string;
  children: React.ReactNode;
  icon?: string;
  height?: string;
}> = ({ title, children, icon, height = '400px' }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    minHeight: height,
  }}>
    <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
      {icon && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '16px',
          padding: '12px',
          marginRight: '16px'
        }}>
          <Icon iconName={icon} style={{ fontSize: '24px', color: '#ffffff' }} />
        </div>
      )}
      <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
        {title}
      </Text>
    </Stack>
    
    <div style={{ 
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '16px',
      padding: '20px',
      minHeight: '300px',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      {children}
    </div>
  </div>
);

const DataTableWidget: React.FC<{
  title: string;
  data: any[];
  columns: IColumn[];
  icon?: string;
}> = ({ title, data, columns, icon }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  }}>
    <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
      {icon && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '16px',
          padding: '12px',
          marginRight: '16px'
        }}>
          <Icon iconName={icon} style={{ fontSize: '24px', color: '#ffffff' }} />
        </div>
      )}
      <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
        {title}
      </Text>
    </Stack>
    
    <div style={{
      background: 'rgba(255, 255, 255, 0.6)',
      borderRadius: '16px',
      overflow: 'hidden',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      <DetailsList
        items={data}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selectionMode={SelectionMode.none}
        isHeaderVisible={true}
        styles={{
          root: {
            '& .ms-DetailsHeader': {
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#ffffff',
              paddingTop: '0'
            },
            '& .ms-DetailsHeader-cell': {
              color: '#ffffff !important',
              fontWeight: '600'
            },
            '& .ms-DetailsList-contentWrapper': {
              background: 'rgba(255, 255, 255, 0.8)'
            }
          }
        }}
      />
    </div>
  </div>
);

export const CostDashboard: React.FC<CostDashboardProps> = ({ className }) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [trends, setTrends] = useState<CostTrend[]>([]);
  const [modelBreakdown, setModelBreakdown] = useState<ModelUsage[]>([]);
  const [userBreakdown, setUserBreakdown] = useState<UserUsage[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [availableModelOptions, setAvailableModelOptions] = useState<{ id: string; label: string }[]>([]);
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);
  
  // Helper to get start of current month
  const getStartOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };
  
  // Filter state - default to current month
  const [filters, setFilters] = useState<Filters>({
    dateRange: {
      startDate: getStartOfCurrentMonth(), // Start of current month
      endDate: new Date(),
    },
    selectedUser: null,
    selectedModel: null,
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load initial data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Reload data when filters change
  useEffect(() => {
    if (!loading) {
      loadDashboardData();
    }
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = filters.dateRange;
      const queryParams: any = {};
      
      if (startDate) queryParams.start_date = startDate.toISOString();
      if (endDate) queryParams.end_date = endDate.toISOString();
      if (filters.selectedUser) queryParams.user_id = filters.selectedUser;
      if (filters.selectedModel) queryParams.model = filters.selectedModel;

      // Load all data in parallel
      const [summaryResult, trendsResult, modelsResult, usersResult] = await Promise.all([
        costService.getSummary(queryParams),
        costService.getTrends(queryParams),
        costService.getModelBreakdown(queryParams),
        costService.getUserBreakdown(queryParams),
      ]);

      setSummary(summaryResult);
      setTrends(trendsResult);
      setModelBreakdown(modelsResult);
      setUserBreakdown(usersResult);

      // Extract available models and users for filter dropdowns
      const models = new Set(modelsResult.map(m => m.model));
      const users = new Set(usersResult.map(u => u.user_id));
      setAvailableModels(Array.from(models));
      setAvailableUsers(Array.from(users));
      try {
        const resp = await fetch(buildApiUrl('/available_models'), { credentials: 'include' });
        const data = await resp.json();
        if (Array.isArray(data?.model_options)) {
          setAvailableModelOptions(data.model_options);
        }
      } catch {}

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load cost data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field: 'startDate' | 'endDate', date: Date | null | undefined) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: date || null,
      },
    }));
  };

  const handleModelFilter = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    setFilters(prev => ({
      ...prev,
      selectedModel: option?.key === 'all' ? null : (option?.key as string),
    }));
  };

  const handleUserFilter = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    setFilters(prev => ({
      ...prev,
      selectedUser: option?.key === 'all' ? null : (option?.key as string),
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: {
        startDate: getStartOfCurrentMonth(), // Reset to current month
        endDate: new Date(),
      },
      selectedUser: null,
      selectedModel: null,
    });
  };

  const exportData = async (format: 'csv' | 'json' = 'csv') => {
    try {
      setExporting(true);
      const { startDate, endDate } = filters.dateRange;
      
      const params: any = {
        format,
      };
      
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      if (filters.selectedUser) params.user_id = filters.selectedUser;

      await costService.exportData(params);
    } catch (err) {
      console.error('Failed to export data:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getFilterSummary = () => {
    const parts: string[] = [];
    if (filters.selectedModel) parts.push(`Model: ${filters.selectedModel}`);
    if (filters.selectedUser) parts.push(`User: ${filters.selectedUser}`);
    
    const { startDate, endDate } = filters.dateRange;
    if (startDate && endDate) {
      const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      parts.push(`Period: ${days} days`);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'Last 30 days';
  };

  // Prepare dropdown options
  const modelOptions: IDropdownOption[] = [
    { key: 'all', text: 'All models' },
    ...availableModels.map(model => {
      const opt = availableModelOptions.find(o => o.id === model);
      return { key: model, text: opt?.label || model };
    })
  ];

  const userOptions: IDropdownOption[] = [
    { key: 'all', text: 'All users' },
    ...availableUsers.map(user => ({ key: user, text: user }))
  ];

  // Define columns for data tables
  const modelColumns: IColumn[] = [
    {
      key: 'model',
      name: 'Model',
      fieldName: 'model',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'requests',
      name: 'Requests',
      fieldName: 'request_count',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'tokens',
      name: 'Total Tokens',
      fieldName: 'total_tokens',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onRender: (item: ModelUsage) => item.total_tokens.toLocaleString(),
    },
    {
      key: 'cost',
      name: 'Total Cost',
      fieldName: 'total_cost',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: ModelUsage) => `$${item.total_cost.toFixed(4)}`,
    },
  ];

  const userColumns: IColumn[] = [
    {
      key: 'user',
      name: 'User',
      fieldName: 'user_id',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'requests',
      name: 'Requests',
      fieldName: 'request_count',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'tokens',
      name: 'Total Tokens',
      fieldName: 'total_tokens',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onRender: (item: UserUsage) => item.total_tokens.toLocaleString(),
    },
    {
      key: 'cost',
      name: 'Total Cost',
      fieldName: 'total_cost',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: UserUsage) => `$${item.total_cost.toFixed(4)}`,
    },
  ];

  if (loading && !summary) {
    return (
      <div style={{ 
        background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
        minHeight: '100vh',
        padding: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '32px',
          padding: '48px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          textAlign: 'center'
        }}>
          <Spinner size={SpinnerSize.large} />
          <Text variant="large" style={{ 
            marginTop: '20px', 
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Loading cost analytics...
          </Text>
          <Text variant="medium" style={{ 
            marginTop: '8px', 
            color: '#718096'
          }}>
            Gathering insights from your AI usage data
          </Text>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const pieChartData = modelBreakdown.map((model, index) => ({
    name: model.model,
    value: model.total_cost,
    cost: model.total_cost,
    requests: model.request_count,
    tokens: model.total_tokens,
    fill: ['#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#ff9a9e'][index % 6]
  }));

  const barChartData = userBreakdown.slice(0, 10).map(user => ({
    user: user.user_id.length > 15 ? user.user_id.substring(0, 15) + '...' : user.user_id,
    cost: user.total_cost,
    requests: user.request_count,
    tokens: user.total_tokens
  }));

  const trendChartData = trends.map(trend => ({
    date: new Date(trend.date).toLocaleDateString(),
    cost: trend.total_cost,
    requests: trend.request_count,
    tokens: trend.total_tokens
  }));

  const colors = ['#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      minHeight: '100vh',
      padding: '32px',
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Modern Glassy Header */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '32px',
        padding: '32px',
        marginBottom: '32px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
      }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack>
            <Text style={{ 
              fontSize: '42px', 
              fontWeight: '800',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px'
            }}>
              AI Cost Analytics
            </Text>
            <Text variant="large" style={{ color: '#4a5568', fontSize: '18px' }}>
              {getFilterSummary()}
            </Text>
          </Stack>
          
          <Stack horizontal tokens={{ childrenGap: 16 }}>
            <DefaultButton
              text={showFilters ? 'Hide Filters' : 'Filters'}
              iconProps={{ iconName: 'Filter' }}
              onClick={() => setShowFilters(!showFilters)}
              styles={{
                root: {
                  borderRadius: '16px',
                  border: '2px solid rgba(102, 126, 234, 0.3)',
                  background: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(10px)',
                  color: '#667eea',
                  fontWeight: '600',
                  padding: '12px 24px',
                  height: '48px'
                },
                rootHovered: {
                  background: 'rgba(102, 126, 234, 0.1)',
                  border: '2px solid #667eea'
                }
              }}
            />
            
            <PrimaryButton
              text={exporting ? 'Exporting...' : 'Export'}
              iconProps={{ iconName: 'Download' }}
              onClick={() => exportData('csv')}
              disabled={exporting}
              styles={{
                root: {
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  fontWeight: '600',
                  padding: '12px 24px',
                  height: '48px',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                }
              }}
            />
          </Stack>
        </Stack>
      </div>

      {/* Error Message */}
      {error && (
        <MessageBar 
          messageBarType={MessageBarType.error} 
          onDismiss={() => setError(null)}
          style={{ 
            marginBottom: '24px',
            borderRadius: '16px',
            border: 'none',
            background: 'rgba(231, 76, 60, 0.1)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {error}
        </MessageBar>
      )}

      {/* Filters Panel */}
      <Panel
        isOpen={showFilters}
        onDismiss={() => setShowFilters(false)}
        type={PanelType.medium}
        headerText="Filter Options"
        closeButtonAriaLabel="Close"
        styles={{
          main: {
            borderRadius: '0 24px 24px 0',
            background: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <Stack tokens={{ childrenGap: 20 }} style={{ padding: '20px 0' }}>
          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="medium" style={{ fontWeight: 'bold' }}>Date Range</Text>
            <DatePicker
              label="Start Date"
              value={filters.dateRange.startDate || undefined}
              onSelectDate={(date) => handleDateRangeChange('startDate', date)}
              placeholder="Select start date"
            />
            <DatePicker
              label="End Date"
              value={filters.dateRange.endDate || undefined}
              onSelectDate={(date) => handleDateRangeChange('endDate', date)}
              placeholder="Select end date"
            />
          </Stack>
          
          <Dropdown
            label="Model"
            options={modelOptions}
            selectedKey={filters.selectedModel || 'all'}
            onChange={handleModelFilter}
          />
          
          <Dropdown
            label="User"
            options={userOptions}
            selectedKey={filters.selectedUser || 'all'}
            onChange={handleUserFilter}
          />
          
          <DefaultButton text="Clear Filters" onClick={clearFilters} />
        </Stack>
      </Panel>

      {/* Main Dashboard Content */}
      <Stack tokens={{ childrenGap: 32 }}>
        {/* Key Metrics Grid */}
        {summary && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '16px'
          }}>
            <MetricCard
              title="Total Cost"
              value={`$${summary.total_cost.toFixed(4)}`}
              subtitle={`${summary.request_count} requests`}
              icon="Money"
              color="#667eea"
              trend={12.5}
            />
            <MetricCard
              title="Total Tokens"
              value={summary.total_tokens.toLocaleString()}
              subtitle={`${summary.average_tokens_per_request.toFixed(0)} avg per request`}
              icon="ChatBot"
              color="#f093fb"
              trend={8.3}
            />
            <MetricCard
              title="Input Cost"
              value={`$${summary.total_input_cost.toFixed(4)}`}
              subtitle={`${summary.total_prompt_tokens.toLocaleString()} tokens`}
              icon="Import"
              color="#4facfe"
              trend={-2.1}
            />
            <MetricCard
              title="Output Cost"
              value={`$${summary.total_output_cost.toFixed(4)}`}
              subtitle={`${summary.total_completion_tokens.toLocaleString()} tokens`}
              icon="Export"
              color="#43e97b"
              trend={15.7}
            />
            <MetricCard
              title="Avg Cost/Request"
              value={`$${summary.average_cost_per_request.toFixed(6)}`}
              subtitle="Per API call"
              icon="Calculator"
              color="#fa709a"
              trend={-5.2}
            />
          </div>
        )}

        {/* Token Limits Progress */}
        <OrganizationUsageProgress />

        {/* Charts Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '32px'
        }}>
          {/* Model Cost Distribution - Horizontal Bar Chart */}
          {pieChartData.length > 0 && (
            <ChartWidget title="Model Cost Distribution" icon="Robot">
              <ResponsiveContainer width="100%" height={Math.max(300, pieChartData.length * 40)}>
                <BarChart 
                  data={pieChartData.sort((a, b) => b.value - a.value)} 
                  layout="vertical"
                  margin={{ left: 20, right: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.3)" horizontal={false} />
                  <XAxis 
                    type="number" 
                    stroke="#4a5568" 
                    fontSize={12}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#4a5568" 
                    fontSize={11}
                    width={120}
                    tick={{ fill: '#4a5568' }}
                  />
                  <Tooltip 
                    formatter={(value: any) => [`$${Number(value).toFixed(4)}`, 'Cost']}
                    labelFormatter={(label) => `Model: ${label}`}
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[0, 4, 4, 0]}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartWidget>
          )}

          {/* User Usage Bar Chart */}
          {barChartData.length > 0 && (
            <ChartWidget title="Top Users by Cost" icon="People">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.3)" />
                  <XAxis 
                    dataKey="user" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    fontSize={12}
                    stroke="#4a5568"
                  />
                  <YAxis stroke="#4a5568" fontSize={12} />
                  <Tooltip 
                    formatter={(value: any, name: any) => {
                      if (name === 'cost') return [`$${Number(value).toFixed(4)}`, 'Cost'];
                      if (name === 'requests') return [Number(value).toLocaleString(), 'Requests'];
                      return [Number(value).toLocaleString(), 'Tokens'];
                    }}
                    labelFormatter={(label) => `User: ${label}`}
                  />
                  <Bar dataKey="cost" fill="#667eea" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartWidget>
          )}
        </div>

        {/* Trends Area Chart */}
        {trendChartData.length > 0 && (
          <ChartWidget title="Cost Trends Over Time" icon="TrendingUp" height="500px">
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={trendChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.3)" />
                <XAxis dataKey="date" stroke="#4a5568" fontSize={12} />
                <YAxis stroke="#4a5568" fontSize={12} />
                <Tooltip 
                  formatter={(value: any) => [`$${Number(value).toFixed(4)}`, 'Cost']}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Area 
                  type="monotone" 
                  dataKey="cost" 
                  stroke="#667eea" 
                  fill="url(#colorCost)"
                  strokeWidth={3}
                />
                <defs>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </ChartWidget>
        )}

        {/* Data Tables */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '32px'
        }}>
          <DataTableWidget
            title="Model Performance Details"
            data={modelBreakdown}
            columns={modelColumns}
            icon="Robot"
          />

          <DataTableWidget
            title="User Activity Details"
            data={userBreakdown}
            columns={userColumns}
            icon="People"
          />
        </div>
      </Stack>
    </div>
  );
};
